import type { MapDefinition, Point } from '../../../shared/game/world-types.js';
import { seededRandom } from '../../../shared/game/seeded-random.js';
import { inkSurvey } from './coast-chart.js';

/** The sheet's square drawing, in pixels; CSS scales it to the screen. */
const SIZE = 1200;
const INK = '#3b2412';
const turn = (x: number, z: number) => ({ x: (x - z) / Math.SQRT2, y: (x + z) / Math.SQRT2 });

/**
 * The loading screen's map: the land round the destination drawn as an old survey filling a sheet of parchment, turned like the camera (north to the
 * upper right), with a compass rose in a corner. The bar's progress is the party's way in, inked as a dashed route along the
 * road that passes nearest the destination, from where it comes onto the sheet to a mark on the spot. Presentation only.
 */
export class LoadingChart {
  readonly canvas = document.createElement('canvas');
  private sheet = document.createElement('canvas');
  private drawn = '';
  /** The route on the drawing, in pixels, and the length run so far along it at each point. */
  private route: { x: number; y: number; run: number }[] = [];
  private mark: { x: number; y: number; name: string } | null = null;
  private fraction = 0;
  constructor() {
    this.canvas.className = 'world-loading-chart'; this.canvas.setAttribute('aria-hidden', 'true');
    this.canvas.width = this.canvas.height = this.sheet.width = this.sheet.height = SIZE;
    // Canvas text quietly falls back while the title face is still loading: draw everything again once it arrives.
    void document.fonts?.load("44px 'Metal Mania'").then(() => { this.drawn = ''; }, () => {});
  }
  /** Draws `map`'s sheet (once per map and destination), then the route as far as `fraction` (0–1). */
  draw(map: MapDefinition, destination: Point, fraction: number) {
    const key = `${map.id}:${destination.x.toFixed(1)}:${destination.z.toFixed(1)}`;
    if (key !== this.drawn) { this.drawn = key; this.drawSheet(map, destination); this.fraction = -1; }
    if (fraction === this.fraction) return;
    this.fraction = fraction;
    const ctx = this.canvas.getContext('2d')!;
    ctx.clearRect(0, 0, SIZE, SIZE); ctx.drawImage(this.sheet, 0, 0);
    this.drawRoute(ctx, fraction);
  }

  private drawSheet(map: MapDefinition, destination: Point) {
    const ctx = this.sheet.getContext('2d')!, random = seededRandom(`loading:${map.id}`);
    ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, SIZE, SIZE);
    // A sheet with a ragged, worn edge.
    const margin = 18, edge: { x: number; y: number }[] = [];
    for (let side = 0; side < 4; side++) for (let step = 0; step < 40; step++) {
      const t = step / 40, wobble = (random() - .5) * 7 + Math.sin(t * 9 + side) * 2.5, inner = SIZE - margin * 2;
      edge.push(side === 0 ? { x: margin + t * inner, y: margin + wobble } : side === 1 ? { x: SIZE - margin + wobble, y: margin + t * inner }
        : side === 2 ? { x: SIZE - margin - t * inner, y: SIZE - margin + wobble } : { x: margin + wobble, y: SIZE - margin - t * inner });
    }
    ctx.save(); ctx.beginPath(); edge.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.clip();
    ctx.fillStyle = '#c9b48a'; ctx.fillRect(0, 0, SIZE, SIZE);
    // Stains and fading: soft blotches lighter and darker than the sheet.
    for (let i = 0; i < 70; i++) {
      const x = random() * SIZE, y = random() * SIZE, r = 40 + random() * 220, dark = random() < .55;
      const wash = ctx.createRadialGradient(x, y, 0, x, y, r);
      wash.addColorStop(0, dark ? `rgba(110,78,40,${.05 + random() * .08})` : `rgba(236,222,186,${.06 + random() * .1})`); wash.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = wash; ctx.fillRect(x - r, y - r, r * 2, r * 2);
    }
    // Fibres in the paper.
    ctx.lineWidth = 1;
    for (let i = 0; i < 900; i++) {
      const x = random() * SIZE, y = random() * SIZE, a = random() * Math.PI, l = 4 + random() * 14;
      ctx.strokeStyle = random() < .5 ? 'rgba(90,62,30,.07)' : 'rgba(245,232,200,.09)';
      ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * l, y + Math.sin(a) * l); ctx.stroke();
    }

    // The map, inked into the paper (multiplied, so the stains show through) and turned like the camera, fills the whole sheet: the
    // sheet is the largest square that stays on the map, a little inside so it can lean towards the destination; the rest is trimmed.
    const half = Math.min(map.width, map.depth) / 2 / Math.SQRT2 * .85, reachX = map.width / 2 - Math.SQRT2 * half, reachZ = map.depth / 2 - Math.SQRT2 * half;
    const center = { x: Math.max(-reachX, Math.min(reachX, destination.x)), z: Math.max(-reachZ, Math.min(reachZ, destination.z)) };
    const scale = SIZE / 2 / half, toSheet = (point: Point) => { const p = turn(point.x - center.x, point.z - center.z); return { x: SIZE / 2 + p.x * scale, y: SIZE / 2 + p.y * scale }; };
    ctx.save(); ctx.globalCompositeOperation = 'multiply'; ctx.globalAlpha = .62;
    ctx.translate(SIZE / 2, SIZE / 2); ctx.scale(scale, scale); ctx.rotate(Math.PI / 4); ctx.translate(-center.x, -center.z);
    inkSurvey(ctx, map, 1 / scale);
    ctx.restore();
    this.drawRose(ctx);

    // Darkened, handled edges.
    const burn = ctx.createRadialGradient(SIZE / 2, SIZE / 2, SIZE * .36, SIZE / 2, SIZE / 2, SIZE * .74);
    burn.addColorStop(0, 'rgba(80,50,20,0)'); burn.addColorStop(.75, 'rgba(80,50,20,.22)'); burn.addColorStop(1, 'rgba(50,28,10,.7)');
    ctx.fillStyle = burn; ctx.fillRect(0, 0, SIZE, SIZE);
    ctx.restore();
    ctx.strokeStyle = 'rgba(40,24,10,.6)'; ctx.lineWidth = 2; ctx.beginPath(); edge.forEach((p, i) => i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)); ctx.closePath(); ctx.stroke();

    this.route = this.onSheet(this.findRoute(map, destination).map(toSheet), margin).map(p => ({ ...p, run: 0 }));
    for (let i = 1; i < this.route.length; i++) this.route[i].run = this.route[i - 1].run + Math.hypot(this.route[i].x - this.route[i - 1].x, this.route[i].y - this.route[i - 1].y);
    const near = (map.landmarks ?? []).map(landmark => ({ landmark, distance: Math.hypot(landmark.x - destination.x, landmark.z - destination.z) })).sort((a, b) => a.distance - b.distance)[0];
    const name = near && near.distance < 120 ? near.landmark.name.split(' · ')[0].toLocaleLowerCase().replace(/(^|\s)\S/g, letter => letter.toLocaleUpperCase()) : '';
    this.mark = { ...toSheet(destination), name };
  }

  /** The route from where it first comes onto the sheet, `margin` pixels inside its edge. */
  private onSheet(route: { x: number; y: number }[], margin: number) {
    const inside = (p: { x: number; y: number }) => p.x >= margin && p.x <= SIZE - margin && p.y >= margin && p.y <= SIZE - margin;
    const first = route.findIndex(inside);
    if (first <= 0) return route;
    // Halve the way along the segment that crosses the edge until the crossing is found to well under a pixel.
    const a = route[first - 1], b = route[first];
    let low = 0, high = 1;
    for (let i = 0; i < 20; i++) { const t = (low + high) / 2; if (inside({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t })) high = t; else low = t; }
    return [{ x: a.x + (b.x - a.x) * high, y: a.y + (b.y - a.y) * high }, ...route.slice(first)];
  }

  /** An eight-point rose in ink in the sheet's lower left corner, its north blade pointing the way world north lies on the sheet. */
  private drawRose(ctx: CanvasRenderingContext2D) {
    const cx = SIZE * .15, cy = SIZE * .85;
    const r = SIZE * .075, north = -Math.PI / 4;
    ctx.save(); ctx.translate(cx, cy);
    ctx.strokeStyle = INK; ctx.fillStyle = INK; ctx.globalAlpha = .8; ctx.lineJoin = 'round';
    ctx.lineWidth = 1.2; ctx.beginPath(); ctx.arc(0, 0, r * .62, 0, Math.PI * 2); ctx.stroke(); ctx.beginPath(); ctx.arc(0, 0, r * .68, 0, Math.PI * 2); ctx.stroke();
    for (let i = 0; i < 8; i++) {
      const a = north + i * Math.PI / 4, length = i % 2 ? r * .55 : r, width = i % 2 ? r * .08 : r * .13;
      const tip = { x: Math.cos(a) * length, y: Math.sin(a) * length }, left = { x: Math.cos(a - Math.PI / 2) * width, y: Math.sin(a - Math.PI / 2) * width };
      // Each blade is half filled, half outlined, as engravers drew them.
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(left.x, left.y); ctx.lineTo(tip.x, tip.y); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(-left.x, -left.y); ctx.lineTo(tip.x, tip.y); ctx.closePath(); ctx.stroke();
    }
    ctx.font = `${Math.round(r * .5)}px 'Metal Mania'`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.fillText('N', Math.cos(north) * r * 1.25, Math.sin(north) * r * 1.25);
    ctx.restore();
  }

  /**
   * The way in: of the roads passing near the destination (or else the nearest road or trail), the one with the longest run up to it,
   * from its far end to its nearest point, then on to the spot.
   */
  private findRoute(map: MapDefinition, destination: Point): Point[] {
    const length = (points: Point[]) => points.reduce((sum, point, i) => i ? sum + Math.hypot(point.x - points[i - 1].x, point.z - points[i - 1].z) : 0, 0);
    const ways: { points: Point[]; distance: number; road: boolean }[] = [];
    for (const path of map.scenery?.paths ?? []) {
      if (path.kind === 'cave') continue;
      let nearest: { segment: number; at: Point; distance: number } | null = null;
      for (let i = 1; i < path.points.length; i++) {
        const a = path.points[i - 1], b = path.points[i], dx = b.x - a.x, dz = b.z - a.z;
        const t = Math.max(0, Math.min(1, ((destination.x - a.x) * dx + (destination.z - a.z) * dz) / (dx * dx + dz * dz || 1)));
        const at = { x: a.x + dx * t, z: a.z + dz * t }, distance = Math.hypot(at.x - destination.x, at.z - destination.z);
        if (!nearest || distance < nearest.distance) nearest = { segment: i, at, distance };
      }
      if (!nearest) continue;
      const fromStart = [...path.points.slice(0, nearest.segment), nearest.at], fromEnd = [...path.points.slice(nearest.segment).reverse(), nearest.at];
      ways.push({ points: length(fromStart) >= length(fromEnd) ? fromStart : fromEnd, distance: nearest.distance, road: path.kind === 'road' });
    }
    const near = ways.filter(way => way.road && way.distance < 40);
    const way = near.sort((a, b) => length(b.points) - length(a.points))[0]
      ?? ways.sort((a, b) => a.distance + (a.road ? 0 : 60) - (b.distance + (b.road ? 0 : 60)))[0];
    return way ? [...way.points, destination] : [destination];
  }

  private drawRoute(ctx: CanvasRenderingContext2D, fraction: number) {
    const route = this.route, total = route.at(-1)?.run ?? 0, reach = total * Math.max(0, Math.min(1, fraction));
    ctx.save(); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    if (route.length > 1 && reach > 0) {
      let head = route[0];
      ctx.beginPath(); ctx.moveTo(route[0].x, route[0].y);
      for (let i = 1; i < route.length; i++) {
        const a = route[i - 1], b = route[i];
        if (b.run <= reach) { ctx.lineTo(b.x, b.y); head = b; continue; }
        const t = (reach - a.run) / Math.max(1e-6, b.run - a.run);
        head = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, run: reach }; ctx.lineTo(head.x, head.y); break;
      }
      ctx.setLineDash([22, 13]); ctx.strokeStyle = INK; ctx.lineWidth = 6; ctx.stroke();
      ctx.setLineDash([]); ctx.fillStyle = INK; ctx.beginPath(); ctx.arc(head.x, head.y, 8, 0, Math.PI * 2); ctx.fill();
    }
    const mark = this.mark;
    if (mark) {
      // The destination: an inked cross in a ring, with its place's name.
      ctx.strokeStyle = INK; ctx.lineWidth = 5; ctx.globalAlpha = .9;
      ctx.beginPath(); ctx.arc(mark.x, mark.y, 21, 0, Math.PI * 2); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(mark.x - 11, mark.y - 11); ctx.lineTo(mark.x + 11, mark.y + 11); ctx.moveTo(mark.x + 11, mark.y - 11); ctx.lineTo(mark.x - 11, mark.y + 11); ctx.stroke();
      if (mark.name) {
        ctx.font = "44px 'Metal Mania'"; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillStyle = INK; ctx.fillText(mark.name, mark.x, mark.y - 50);
      }
    }
    ctx.restore();
  }
}
